import { altitudeGroups, categories, productsFlat } from '@fe/weather';
import React from 'react';
import { EventBus } from '../../contexts/event-bus';
import { useFocusedWorkspaceSelector } from '../../features/home/hooks/use-view';
import { useAppSelector } from '../../hooks/store-hooks';
import { Command } from './types';

const MIN_TERM_LENGTH = 2;
const MAX_RESULTS = 15;
const GROUP_QUALIFIER = 0xffff;

const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

type WeatherToggleItem = {
    /** Qualified id as stored in view.weather.all (raw id for products, group + 0xffff for groups). */
    qualifiedId: number;
    name: string;
    category: number;
    /** All product ids that belong to this entry (a single product, or every altitude band of a group). */
    productIds: number[];
    fire: () => void;
};

// Build the toggleable catalog once: standalone products plus one entry per altitude group
const weatherCatalog: WeatherToggleItem[] = (() => {
    const items: WeatherToggleItem[] = [];
    const seenGroups = new Set<number>();

    for (const product of productsFlat) {
        if (product.group === null) {
            items.push({
                qualifiedId: product.id,
                name: product.name,
                category: product.category,
                productIds: [product.id],
                fire: () => EventBus.fire('toggle-weather-product', { productId: product.id })
            });
            continue;
        }

        if (seenGroups.has(product.group)) {
            continue;
        }
        seenGroups.add(product.group);

        const group = altitudeGroups[product.group];
        if (!group) {
            items.push({
                qualifiedId: product.id,
                name: product.name,
                category: product.category,
                productIds: [product.id],
                fire: () => EventBus.fire('toggle-weather-product', { productId: product.id })
            });
            continue;
        }

        const groupId = product.group;
        items.push({
            qualifiedId: groupId + GROUP_QUALIFIER,
            name: group.name,
            category: product.category,
            productIds: productsFlat.filter((x) => x.group === groupId).map((x) => x.id),
            fire: () => EventBus.fire('toggle-weather-product', { groupId, fallbackProductId: product.id })
        });
    }

    return items;
})();

export function useWeatherCommands(query: string): Command[] {
    const weather = useFocusedWorkspaceSelector((x) => x.view.weather);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    const authorized = useAppSelector((x) => x.weather.authorized);
    const playbackAvailable = useAppSelector((x) => x.weather.playback.available);

    return React.useMemo<Command[]>(() => {
        const term = query.trim().toLowerCase();
        if (term.length < MIN_TERM_LENGTH) {
            return [];
        }

        const availableIds = new Set(isPlayback ? playbackAvailable : authorized);
        const activeIds = new Set(weather.all.filter((id) => !weather.hidden.includes(id)));

        return weatherCatalog
            .filter((item) => item.name.toLowerCase().includes(term) && item.productIds.some((id) => availableIds.has(id)))
            .slice(0, MAX_RESULTS)
            .map((item) => ({
                id: `weather-${item.qualifiedId}`,
                title: item.name,
                subtitle: categoryNameById.get(item.category),
                keywords: ['weather', 'product', item.name],
                group: 'Weather',
                checked: activeIds.has(item.qualifiedId),
                run: item.fire
            }));
    }, [query, weather, isPlayback, authorized, playbackAvailable]);
}
